import React from 'react';
import { Box, Container, Grid, GridItem, Heading, Text, SimpleGrid, Icon } from '@chakra-ui/react';
import { Building2, CircleDollarSign, PieChart, ShieldCheck } from 'lucide-react';
import SectionHeading from '../ui/SectionHeading';
import { useStaggeredAnimation } from '../../hooks/useScrollAnimation';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Secure & Transparent',
    description: 'Encrypted ownership records and real-time portfolio transparency.',
  },
  {
    icon: Building2,
    title: 'Institutional Quality',
    description: 'Curated high-quality properties with strong fundamentals and prime locations.',
  },
  {
    icon: CircleDollarSign,
    title: 'Passive Income',
    description: 'Earn consistent rental income with professional property management.',
  },
  {
    icon: PieChart,
    title: 'Diversified Portfolio',
    description: 'Spread your investments across multiple asset types and geographies.',
  },
];

const InlineFeature = ({ icon, title, description, animationStyle }) => (
  <Box
    role="group"
    pl={{ base: 0, lg: 6 }}
    pt={{ base: 6, lg: 0 }}
    borderLeft={{ base: 'none', lg: '1px solid' }}
    borderTop={{ base: '1px solid rgba(255,255,255,0.06)', lg: 'none' }}
    borderColor="rgba(255,255,255,0.06)"
    _first={{ borderLeft: 'none', borderTop: 'none', pl: 0, pt: 0 }}
    style={animationStyle}
  >
    <Box
      w="44px"
      h="44px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="10px"
      bg="rgba(212,168,95,0.08)"
      border="1px solid"
      borderColor="rgba(212,168,95,0.14)"
      mb={4}
      transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
      _groupHover={{
        bg: 'rgba(212,168,95,0.16)',
        borderColor: 'rgba(212,168,95,0.32)',
      }}
    >
      <Icon as={icon} boxSize={5} color="#D4A85F" strokeWidth={1.7} />
    </Box>
    <Box
      h="1px"
      w="24px"
      bg="linear-gradient(90deg, #D4A85F 0%, transparent 100%)"
      mb={2.5}
      transition="width 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
      _groupHover={{ w: '40px' }}
    />
    <Heading
      as="h3"
      fontFamily="heading"
      fontSize={{ base: '1.2rem', md: '1.35rem' }}
      fontWeight={500}
      color="#F5F7FA"
      mb={2}
      letterSpacing="0"
      lineHeight={1.12}
      transition="color 0.25s ease"
      _groupHover={{ color: '#E0B873' }}
    >
      {title}
    </Heading>
    <Text
      fontFamily="body"
      fontSize="sm"
      color="#9AA7B8"
      lineHeight={1.6}
    >
      {description}
    </Text>
  </Box>
);

const WhyChooseSection = () => {
  const [gridRef, visibleItems] = useStaggeredAnimation(FEATURES.length, {
    staggerDelay: 80,
    threshold: 0.15,
  });

  return (
    <Box as="section" py={{ base: 5, md: 5 }} bg="#071326">
      <Container maxW="container.xl" px={{ base: 5, md: 8 }}>
        <Box
          bg="#0A1B34"
          border="1px solid"
          borderColor="rgba(255,255,255,0.10)"
          borderRadius="8px"
          px={{ base: 5, md: 7 }}
          py={{ base: 6, md: 7 }}
        >
          <Grid
            templateColumns={{ base: '1fr', lg: '3fr 9fr' }}
            gap={{ base: 8, lg: 8, xl: 10 }}
            alignItems="start"
          >
            <GridItem>
              <SectionHeading
                align="left"
                size="lg"
                title={<>Why Investors<br />Choose Us</>}
                subtitle="We combine expertise, technology and transparency to deliver exceptional investment experiences."
                mb={0}
              />
            </GridItem>

            <GridItem>
              <SimpleGrid
                ref={gridRef}
                columns={{ base: 1, sm: 2, lg: 4 }}
                spacing={{ base: 7, md: 7, lg: 0 }}
              >
                {FEATURES.map((f, i) => (
                  <InlineFeature
                    key={f.title}
                    icon={f.icon}
                    title={f.title}
                    description={f.description}
                    animationStyle={{
                      opacity: visibleItems.has(i) ? 1 : 0,
                      transform: visibleItems.has(i) ? 'translateY(0)' : 'translateY(12px)',
                      transition:
                        'opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  />
                ))}
              </SimpleGrid>
            </GridItem>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseSection;
